/**
 * @file useDesktopPins.ts
 * @description Reads the signed-in user's `desktopPins`. Consumers:
 * `useDesktopPin`, the settings page "Files on my computer", and the nudge,
 * which asks only whether the user has any pin at all.
 *
 * It listens on `users/{uid}` — the same registry-shared document store
 * `UserProvider` already holds open for the session, so this adds a refcount
 * rather than a second listener. Reading `snap.data?.desktopPins` subscribes to
 * that one key: a `modifiedAt` bump from an unrelated write to the user
 * document does not re-render the pin surfaces.
 *
 * The returned map is a frozen valtio snapshot. Write through `keep` and
 * `stopKeeping`, never here.
 */

import { useContext, useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';

import type { DesktopPin, User } from '#types';
import { UserContext } from '../../providers/UserContext';
import { createDocumentStore, type DocumentStore } from '../../store';

export type DesktopPinsState = {
    /** Keyed by pin id, as stored. Empty while loading, and for a signed-out reader. */
    pins: Readonly<Record<string, DesktopPin>>;
    loading: boolean;
};

const NO_PINS: Readonly<Record<string, DesktopPin>> = {};

const inactiveStore = createDocumentStore<User>('users/__inactive__');

export const useDesktopPins = (): DesktopPinsState => {
    const { user } = useContext(UserContext);
    const uid = user?.uid;

    const store = useMemo<DocumentStore<User>>(
        () => uid ? createDocumentStore<User>(`users/${uid}`) : inactiveStore,
        [uid],
    );

    useEffect(() => {
        if (!uid) return;
        store.connect();
        return () => store.disconnect();
    }, [uid, store]);

    const snap = useSnapshot(store.state);
    const pins = snap.data?.desktopPins as Readonly<Record<string, DesktopPin>> | undefined;

    return { pins: pins ?? NO_PINS, loading: uid ? snap.loading : false };
};
